import { Injectable, NgZone } from '@angular/core';
// @ts-ignore
import ReceiptPrinterEncoder from '@point-of-sale/receipt-printer-encoder';
import { printerModels } from './webserial-printer.service';

// Minimal helpers for USB printing. We avoid strict DOM typings to keep Angular build happy on older TS.
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
type USBDeviceLike = any;

@Injectable({ providedIn: 'root' })
export class WebUsbPrinterService {
  private _encoder: any;
  private encoderData: any;

  // Active USB device/session details
  private device: USBDeviceLike | null = null;
  private interfaceNumber: number | null = null;
  private endpointOut: number | null = null;
  private connected = false;

  private readonly aliasStorageKey = 'printerAliases';
  private readonly lastUsbDeviceKey = 'lastUsedUsbDevice';

  constructor(private zone: NgZone) {
    // Try restore last used device descriptor (only ids, not a live handle)
    const last = localStorage.getItem(this.lastUsbDeviceKey);
    if (last) {
      try {
        this._lastUsedDevice = JSON.parse(last);
      } catch {}
    }

    // Listen USB connect/disconnect to refresh connection state
    if (this.isSupported) {
      try {
        (navigator as any).usb?.addEventListener?.('disconnect', (event: any) => {
          if (this.device && event?.device && this.matchesLastUsed(event.device)) {
            this.connected = false;
            this.device = null;
            this.interfaceNumber = null;
            this.endpointOut = null;
          }
        });
      } catch {}
    }
  }

  // Lazy encoder
  public get encoder(): any {
    if (!this._encoder) {
      this._encoder = new ReceiptPrinterEncoder();
    }
    return this._encoder;
  }

  private _lastUsedDevice: { vendorId?: number | null; productId?: number | null } | null = null;

  get lastDevice(): { vendorId?: number | null; productId?: number | null } | null {
    return this._lastUsedDevice;
  }

  setDefaultPrinter(printer?: { vendorId: number | null; productId: number | null } | null) {
    this._lastUsedDevice = printer ?? null;
    try {
      if (!printer || (printer.vendorId == null && printer.productId == null)) {
        localStorage.removeItem(this.lastUsbDeviceKey);
      } else {
        localStorage.setItem(this.lastUsbDeviceKey, JSON.stringify(printer));
      }
    } catch {}
  }

  get isSupported(): boolean {
    return typeof navigator !== 'undefined' && !!(navigator as any).usb;
  }

  // Identify a printer by vendor/product id, using known models + alias override
  identifyPrinter(info?: { vendorId?: number | null; productId?: number | null; productName?: string | null } | null): string {
    if (!info) return 'Unknown';
    const key = this.getPrinterKey(info);
    const alias = this.getPrinterAlias(info);
    const model = printerModels[key];
    const baseName = info.productName || model || `USB Printer (${key})`;

    if (alias) {
      return alias + (baseName !== 'Unknown' && baseName !== alias ? ` (${baseName})` : '');
    }
    return baseName;
  }

  private getPrinterKey(info: { vendorId?: number | null; productId?: number | null }): string {
    const v = info?.vendorId ?? 0;
    const p = info?.productId ?? 0;
    return `${v}:${p}`;
  }

  private readAliases(): Record<string, string> {
    try {
      const raw = localStorage.getItem(this.aliasStorageKey);
      if (!raw) return {};
      const parsed = JSON.parse(raw);
      return parsed && typeof parsed === 'object' ? (parsed as Record<string, string>) : {};
    } catch {
      return {};
    }
  }

  private writeAliases(map: Record<string, string>): void {
    try {
      localStorage.setItem(this.aliasStorageKey, JSON.stringify(map));
    } catch {}
  }

  getPrinterAlias(info: { vendorId?: number | null; productId?: number | null }): string | undefined {
    const map = this.readAliases();
    return map[this.getPrinterKey(info)];
  }

  setPrinterAlias(info: { vendorId?: number | null; productId?: number | null }, alias?: string): void {
    const map = this.readAliases();
    const key = this.getPrinterKey(info);
    if (!alias) delete map[key];
    else map[key] = alias;
    this.writeAliases(map);
  }

  // Discovery
  async getGrantedDevices(): Promise<{ device: USBDeviceLike; info: { usbVendorId?: number; usbProductId?: number } }[]> {
    if (!this.isSupported) return [];
    const devices: USBDeviceLike[] = await (navigator as any).usb.getDevices();
    const results = [];
    for (const d of devices) {
      const vendorId = (d as any).vendorId ?? (d as any).usbVendorId;
      const productId = (d as any).productId ?? (d as any).usbProductId;
      if (vendorId != null && productId != null) {
        const key = `${vendorId}:${productId}`;
        results.push({ device: d, info: { usbVendorId: vendorId, usbProductId: productId, productName: d.productName } });
      }
    }
    return results;
  }

  async requestDevice(filters?: { usbVendorId?: number; usbProductId?: number }[]): Promise<USBDeviceLike | null> {
    if (!this.isSupported) throw new Error('WebUSB is not supported by this browser.');

    // WebUSB expects filter keys vendorId/productId
    let useFilters = (
      filters && filters.length
        ? filters
        : Object.keys(printerModels)
            .filter(k => k !== '0:0')
            .map(k => {
              const [v, p] = k.split(':').map(n => Number(n));
              return { vendorId: v, productId: p } as any;
            })
    )
      .map(f => ({ vendorId: (f as any).usbVendorId ?? (f as any).vendorId, productId: (f as any).usbProductId ?? (f as any).productId }))
      .filter(f => typeof f.vendorId === 'number' && typeof f.productId === 'number');

    const opts: any = useFilters.length ? { filters: [] } : {};
    const device = await (navigator as any).usb.requestDevice(opts).catch(() => null);
    console.log('device', device);
    return device || null;
  }

  // Connection
  async open(): Promise<void> {
    if (!this.isSupported) throw new Error('WebUSB is not supported by this browser.');

    // If we don't have a live device handle, try to find one that matches lastUsed ids
    if (!this.device) {
      const devices: USBDeviceLike[] = await (navigator as any).usb.getDevices();
      const match = devices.find(d => this.matchesLastUsed(d));
      this.device = match || null;
    }

    // If still none, prompt user
    if (!this.device) {
      const dev = await this.requestDevice();
      if (!dev) throw new Error('No device selected.');
      this.device = dev;
      this._lastUsedDevice = { vendorId: (dev as any).vendorId, productId: (dev as any).productId };
      localStorage.setItem(this.lastUsbDeviceKey, JSON.stringify(this._lastUsedDevice));
    }

    if (!this.device) throw new Error('USB device not available.');

    // Open the device/session
    if (!(this.device as any).opened) {
      await (this.device as any).open();
    }

    // Select a configuration if not already selected
    if (!(this.device as any).configuration) {
      await (this.device as any).selectConfiguration(1);
    }

    // Find first interface with an OUT bulk endpoint (prefer printer or vendor class)
    let chosenInterface: any | null = null;
    let endpointOut: number | null = null;

    const interfaces: any[] = (this.device as any).configuration?.interfaces || [];
    for (const iface of interfaces) {
      const alternates: any[] = iface.alternates || [];
      for (const alt of alternates) {
        const cls = alt.interfaceClass; // 0x07 printer, 0xFF vendor-specific
        const eps: any[] = alt.endpoints || [];
        const outEp = eps.find(e => e.direction === 'out' && e.type === 'bulk');
        if (outEp && (cls === 0x07 || cls === 0xff || cls === 7 || cls === 255)) {
          chosenInterface = { number: iface.interfaceNumber };
          endpointOut = outEp.endpointNumber;
          break;
        }
      }
      if (chosenInterface && endpointOut != null) break;
    }

    // Fallback: pick any bulk OUT endpoint
    if (!chosenInterface || endpointOut == null) {
      for (const iface of interfaces) {
        const alternates: any[] = iface.alternates || [];
        for (const alt of alternates) {
          const outEp = (alt.endpoints || []).find((e: any) => e.direction === 'out' && e.type === 'bulk');
          if (outEp) {
            chosenInterface = { number: iface.interfaceNumber };
            endpointOut = outEp.endpointNumber;
            break;
          }
        }
        if (chosenInterface && endpointOut != null) break;
      }
    }

    if (!chosenInterface || endpointOut == null) {
      throw new Error('Could not find a suitable USB interface/endpoint for printing.');
    }

    await (this.device as any).claimInterface(chosenInterface.number);
    this.interfaceNumber = chosenInterface.number;
    this.endpointOut = endpointOut;
    this.connected = true;
  }

  ensureOpen(): Promise<void> | void {
    if (!this.connected) {
      return this.open();
    }
  }

  private matchesLastUsed(d: USBDeviceLike): boolean {
    if (!this._lastUsedDevice) return false;
    const v = (d as any).vendorId ?? (d as any).usbVendorId;
    const p = (d as any).productId ?? (d as any).usbProductId;
    return v === this._lastUsedDevice.vendorId && p === this._lastUsedDevice.productId;
  }

  // Printing helpers (mirror WebSerialPrinterService API shape)
  table(columns: { width: number; align: 'left' | 'right' | 'center' }[]) {
    if (!this.encoderData) {
      this.encoderData = this.encoder.initialize();
    }
    return new (class TableBuilder {
      constructor(
        private enc: any,
        private cols: { width: number; align: 'left' | 'right' | 'center' }[]
      ) {}
      row(...cells: string[]) {
        const line = cells
          .map((text, i) => TableBuilder.formatCell(String(text ?? ''), this.cols[i]?.width ?? 10, this.cols[i]?.align ?? 'left'))
          .join('');
        this.enc = this.enc.line(line);
        return this;
      }
      static formatCell(text: string, width: number, align: 'left' | 'right' | 'center'): string {
        if (text.length > width) return text.substring(0, width);
        const space = width - text.length;
        switch (align) {
          case 'right':
            return ' '.repeat(space) + text;
          case 'center':
            const left = Math.floor(space / 2);
            return ' '.repeat(left) + text + ' '.repeat(space - left);
          default:
            return text + ' '.repeat(space);
        }
      }
    })(this.encoderData, columns);
  }

  printLines(lines: string[], opts?: { align?: 'left' | 'center' | 'right'; bold?: boolean }) {
    if (!this.encoderData) {
      this.encoderData = this.encoder.initialize();
    }
    if (opts?.align) this.encoderData = this.encoderData.align(opts.align);
    if (opts?.bold) this.encoderData = this.encoderData.bold(true);
    for (const line of lines) {
      this.encoderData = this.encoderData.line(line);
      this.encoderData = this.encoderData.newline();
    }
    if (opts?.bold) this.encoderData = this.encoderData.bold(false);
  }

  feed(lines = 3) {
    if (this.encoderData?.feed) this.encoderData = this.encoderData.feed(lines);
  }

  private finalize(): Uint8Array {
    // Ensure a few newlines and cut
    let data = this.encoderData.newline().newline().newline().newline().newline().newline().newline().cut().encode();
    return data;
  }

  async print(): Promise<void> {
    if (!this.isSupported) throw new Error('WebUSB unsupported');
    if (!this.connected) return; // open is triggered by ensureOpen
    const data = this.finalize();
    await this.sendRaw(data);
    this.encoderData = null;
  }

  async sendRaw(data: Uint8Array): Promise<void> {
    if (!this.device || this.endpointOut == null) throw new Error('USB printer not connected');
    // Chunk large payloads to avoid issues with some devices
    const chunkSize = 4096;
    for (let i = 0; i < data.byteLength; i += chunkSize) {
      const chunk = data.subarray(i, Math.min(i + chunkSize, data.byteLength));
      await (this.device as any).transferOut(this.endpointOut, chunk);
    }
  }

  async testPrint() {
    this.encoderData = this.encoder.initialize();
    this.encoderData = this.encoderData.align('center');
    this.encoderData = this.encoderData.bold(true).line('Teste impressao').bold(false);
    this.table([
      { width: 12, align: 'left' },
      { width: 32, align: 'right' }
    ]).row(`Impressora:`, `${this.identifyPrinter(this._lastUsedDevice || {})}`);
    this.table([{ width: 80, align: 'center' }]).row(`NosERP`);
    this.encoderData = this.encoderData.newline().line('Obrigado!');
    await (this.ensureOpen() as any);
    await this.print();
  }

  async printCanvases(
    canvases: HTMLCanvasElement[],
    opts?: {
      align?: 'left' | 'center' | 'right';
      dither?: boolean;
      marginTopPx?: number;
      marginBottomPx?: number;
      cut?: 'full' | 'partial' | false;
    }
  ) {
    const { align = 'center', dither = false, marginTopPx = 8, marginBottomPx = 16, cut = 'full' } = opts || {};

    this.encoderData = this.encoder.initialize();
    this.encoderData = this.encoderData.align(align);

    for (let canvas of canvases) {
      let src = canvas;
      if (src.width % 8 !== 0) {
        const fixedWidth = Math.ceil(src.width / 8) * 8;
        const scale = fixedWidth / src.width;
        const newHeight = Math.round(src.height * scale);
        const resized = document.createElement('canvas');
        resized.width = fixedWidth;
        resized.height = newHeight;
        const ctx = resized.getContext('2d');
        if (!ctx) throw new Error('Cannot get canvas context');
        ctx.fillStyle = '#FFFFFF';
        ctx.fillRect(0, 0, resized.width, resized.height);
        ctx.drawImage(src, 0, 0, fixedWidth, newHeight);
        src = resized;
      }
      if (marginTopPx > 0) {
        const lines = Math.max(0, Math.round(marginTopPx / 8));
        if (lines > 0 && this.encoderData.feed) this.encoderData = this.encoderData.feed(lines);
      }
      this.encoderData = this.encoderData.image(src, 64, 64, dither ? 'atkinson' : 'threshold');
      if (marginBottomPx > 0) {
        const lines = Math.max(0, Math.round(marginBottomPx / 8));
        if (lines > 0 && this.encoderData.feed) this.encoderData = this.encoderData.feed(lines);
      }
    }

    if (cut) {
      this.encoderData = this.encoderData.cut(cut);
    }

    await this.open();
    const data = this.encoderData.encode();
    await this.sendRaw(data);
  }
}
